import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, KeyRound, Lock, LogOut, RefreshCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const STORAGE_KEY = 'admin_services_password';

const AdminPassword = () => {
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  const [changeForm, setChangeForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const [resetForm, setResetForm] = useState({
    newPassword: '',
    confirmNewPassword: '',
  });

  const verifyPassword = useCallback(async (password, silent = false) => {
    if (!password) {
      if (!silent) {
        setStatus({ type: 'error', text: 'Enter your admin password.' });
      }
      setIsCheckingAuth(false);
      setIsAuthenticated(false);
      return false;
    }

    setIsVerifying(true);

    try {
      const response = await fetch('/api/admin/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error(`Auth failed (${response.status})`);
      }

      sessionStorage.setItem(STORAGE_KEY, password);
      setAdminPassword(password);
      setIsAuthenticated(true);
      setStatus({ type: '', text: '' });
      return true;
    } catch (error) {
      console.error(error);
      sessionStorage.removeItem(STORAGE_KEY);
      setIsAuthenticated(false);
      if (!silent) {
        setStatus({ type: 'error', text: 'Invalid admin password.' });
      }
      return false;
    } finally {
      setIsVerifying(false);
      setIsCheckingAuth(false);
    }
  }, []);

  useEffect(() => {
    const storedPassword = sessionStorage.getItem(STORAGE_KEY);

    if (!storedPassword) {
      setIsCheckingAuth(false);
      return;
    }

    setAdminPassword(storedPassword);
    verifyPassword(storedPassword, true);
  }, [verifyPassword]);

  const handleSignIn = async (event) => {
    event.preventDefault();
    await verifyPassword(adminPassword, false);
  };

  const handleSignOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    setAdminPassword('');
    setChangeForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    setResetForm({ newPassword: '', confirmNewPassword: '' });
    setStatus({ type: '', text: '' });
  };

  const handleReloadSession = async () => {
    await verifyPassword(adminPassword, false);
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();

    if (!changeForm.currentPassword || !changeForm.newPassword || !changeForm.confirmNewPassword) {
      setStatus({ type: 'error', text: 'Fill in all change-password fields.' });
      return;
    }

    if (changeForm.newPassword.length < 8) {
      setStatus({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }

    if (changeForm.newPassword !== changeForm.confirmNewPassword) {
      setStatus({ type: 'error', text: 'New password confirmation does not match.' });
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await fetch('/api/admin/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          currentPassword: changeForm.currentPassword,
          newPassword: changeForm.newPassword,
        }),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.detail || `Failed to change password (${response.status})`);
      }

      sessionStorage.setItem(STORAGE_KEY, changeForm.newPassword);
      setAdminPassword(changeForm.newPassword);
      setChangeForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
      setStatus({ type: 'success', text: 'Password changed successfully.' });
    } catch (error) {
      console.error(error);
      setStatus({ type: 'error', text: error.message || 'Failed to change password.' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (!resetForm.newPassword || !resetForm.confirmNewPassword) {
      setStatus({ type: 'error', text: 'Fill in all reset-password fields.' });
      return;
    }

    if (resetForm.newPassword.length < 8) {
      setStatus({ type: 'error', text: 'Reset password must be at least 8 characters.' });
      return;
    }

    if (resetForm.newPassword !== resetForm.confirmNewPassword) {
      setStatus({ type: 'error', text: 'Reset password confirmation does not match.' });
      return;
    }

    setIsResettingPassword(true);

    try {
      const response = await fetch('/api/admin/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Admin-Password': adminPassword,
        },
        body: JSON.stringify({
          newPassword: resetForm.newPassword,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          setIsAuthenticated(false);
          setStatus({ type: 'error', text: 'Admin session expired. Please sign in again.' });
          return;
        }

        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.detail || `Failed to reset password (${response.status})`);
      }

      sessionStorage.setItem(STORAGE_KEY, resetForm.newPassword);
      setAdminPassword(resetForm.newPassword);
      setResetForm({ newPassword: '', confirmNewPassword: '' });
      setStatus({ type: 'success', text: 'Password reset successfully.' });
    } catch (error) {
      console.error(error);
      setStatus({ type: 'error', text: error.message || 'Failed to reset password.' });
    } finally {
      setIsResettingPassword(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200">
            <CardContent className="p-6 text-sage-700">Checking admin session...</CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sage-900">
                <Lock className="w-5 h-5" />
                Admin Access
              </CardTitle>
              <CardDescription>
                Enter the admin password to manage password settings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Label className="text-sage-700">Password</Label>
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(event) => setAdminPassword(event.target.value)}
                    className="mt-1.5 border-sage-300"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-sage-600 hover:bg-sage-700"
                  disabled={isVerifying}
                >
                  {isVerifying ? 'Verifying...' : 'Sign In'}
                </Button>
                {status.text && (
                  <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {status.text}
                  </p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sage-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-sage-900">Admin Password</h1>
            <p className="text-sage-600 mt-1">Change or reset your admin login password.</p>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button type="button" variant="outline" className="border-sage-300">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin Home
              </Button>
            </Link>
            <Button
              type="button"
              variant="outline"
              onClick={handleReloadSession}
              disabled={isVerifying}
              className="border-sage-300"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Re-Verify
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleSignOut}
              className="border-sage-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>

        {status.text && (
          <div
            className={`rounded-lg px-4 py-3 text-sm font-medium ${
              status.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {status.text}
          </div>
        )}

        <Card className="border-sage-200 shadow-sm">
          <CardHeader className="bg-white border-b border-sage-200">
            <CardTitle className="text-sage-900 flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Change Password
            </CardTitle>
            <CardDescription>
              Use this when you know your current password.
            </CardDescription>
          </CardHeader>
          <CardContent className="bg-white pt-6">
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label className="text-sage-700">Current Password</Label>
                <Input
                  type="password"
                  value={changeForm.currentPassword}
                  onChange={(event) =>
                    setChangeForm((current) => ({ ...current, currentPassword: event.target.value }))
                  }
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">New Password</Label>
                <Input
                  type="password"
                  value={changeForm.newPassword}
                  onChange={(event) =>
                    setChangeForm((current) => ({ ...current, newPassword: event.target.value }))
                  }
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">Confirm New Password</Label>
                <Input
                  type="password"
                  value={changeForm.confirmNewPassword}
                  onChange={(event) =>
                    setChangeForm((current) => ({ ...current, confirmNewPassword: event.target.value }))
                  }
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <Button
                type="submit"
                className="bg-sage-600 hover:bg-sage-700 text-white"
                disabled={isChangingPassword}
              >
                {isChangingPassword ? 'Updating...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-sage-200 shadow-sm">
          <CardHeader className="bg-white border-b border-sage-200">
            <CardTitle className="text-sage-900">Reset Password</CardTitle>
            <CardDescription>
              Force-set a new password for the current admin account.
            </CardDescription>
          </CardHeader>
          <CardContent className="bg-white pt-6">
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label className="text-sage-700">New Password</Label>
                <Input
                  type="password"
                  value={resetForm.newPassword}
                  onChange={(event) =>
                    setResetForm((current) => ({ ...current, newPassword: event.target.value }))
                  }
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">Confirm New Password</Label>
                <Input
                  type="password"
                  value={resetForm.confirmNewPassword}
                  onChange={(event) =>
                    setResetForm((current) => ({ ...current, confirmNewPassword: event.target.value }))
                  }
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <Button
                type="submit"
                className="bg-earth-600 hover:bg-earth-700 text-white"
                disabled={isResettingPassword}
              >
                {isResettingPassword ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPassword;
